import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  UnitReferenceStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {BaseTypedValueType, TableColumnType} from 'notebook/tables/TableConstants';
import BaseFillPlateTransferStructuredTableAdapter, {
  BaseFillPlateTransferStructuredTableMetadata,
  BaseFillPlateTransferStructuredTableRowMetadata,
} from 'samples/plate-maps/transfer-table/BaseFillPlateTransferStructuredTableAdapter';
import {MULTIPLE_VALUES_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface SummaryViewStructuredTableRowMetadata
  extends BaseFillPlateTransferStructuredTableRowMetadata {
  schemaGroupingId: string;
}

export type SummaryViewStructuredTable = StructuredTable<
  BaseFillPlateTransferStructuredTableMetadata,
  SummaryViewStructuredTableRowMetadata
>;

/**
 * Adapter describing & constraining the behavior of the Summary View Transfer Table in the Fill Plate modal.
 *
 * This table is never embedded in Notebook and never persisted. Its content is determined by a combination of
 * preexisting values of Inventory items and user selections made in the Fill Plate modal.
 */
export default class SummaryViewStructuredTableAdapter extends BaseFillPlateTransferStructuredTableAdapter<
  BaseFillPlateTransferStructuredTableMetadata,
  SummaryViewStructuredTableRowMetadata
> {
  static getAdapter(): SummaryViewStructuredTableAdapter {
    return getStructuredTableAdapters().FILL_PLATE_TRANSFER_SUMMARY_VIEW as SummaryViewStructuredTableAdapter;
  }

  injectFrontEndMetadata({column}: {column: StructuredTableColumn}): StructuredTableColumn {
    const partialColumn = super.injectFrontEndMetadata({column});
    if (partialColumn.columnType === TableColumnType.UNIT_REFERENCE) {
      const unitReferenceColumn = partialColumn as UnitReferenceStructuredTableColumn;
      if (
        unitReferenceColumn.columnGroup === StructuredTableColumnGroups.FILL_PLATE_SOURCE_CONCENTRATION ||
        unitReferenceColumn.columnGroup === StructuredTableColumnGroups.FILL_PLATE_TRANSFER_QUANTITY ||
        unitReferenceColumn.columnGroup === StructuredTableColumnGroups.FILL_PLATE_TARGET_CONCENTRATION
      ) {
        return {
          ...unitReferenceColumn,
          frontEndMetadata: {
            ...unitReferenceColumn.frontEndMetadata,
            ignoreErrorWhenTypedValue: {type: BaseTypedValueType.STRING, rawValue: MULTIPLE_VALUES_SENTINEL},
          },
        };
      }
    }
    return partialColumn;
  }

  getBlankRowMetadata(): SummaryViewStructuredTableRowMetadata {
    return {schemaGroupingId: '', schemaId: ''};
  }

  _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: BaseFillPlateTransferStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
  }): Array<StructuredTableColumn> {
    const baseColumns = super._getAllSupportedColumns(params);
    return [
      baseColumns[0], // icon
      makeStructuredTableColumn({
        name: 'Source',
        columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP,
        isReadOnly: true,
        width: 200,
      }),
      ...baseColumns.slice(1),
    ];
  }
}
